import axios from 'axios';
import { publicApi, DEFAULT_TIMEOUT } from './api';
import queryClient from './queryClient';

const API_BASE = process.env.REACT_APP_API_URL || '/api';

export const CANDIDATE_LOGIN_PATH = '/candidate/login';

// ── In-memory candidate token store (never localStorage) ──
let candidateToken = null;

export function getCandidateToken() { return candidateToken; }
export function setCandidateToken(token) { candidateToken = token; }
export function clearCandidateToken() { candidateToken = null; }

const candidateApi = axios.create({
  baseURL: API_BASE,
  withCredentials: false,
  timeout: DEFAULT_TIMEOUT,
});

candidateApi.interceptors.request.use(config => {
  if (candidateToken) config.headers.Authorization = `Bearer ${candidateToken}`;
  return config;
});

// Candidate access tokens are short-lived and there is no refresh cookie for
// the portal, so a 401 is terminal: drop the token and send the candidate to
// the login screen instead of leaving the portal showing zero assessments (or
// silently discarding a submitted answer).
let redirecting = false;

function redirectToCandidateLogin() {
  clearCandidateToken();
  queryClient.clear();
  if (redirecting) return;
  const { pathname, search } = window.location;
  if (pathname.startsWith(CANDIDATE_LOGIN_PATH)) return;
  redirecting = true;
  const next = encodeURIComponent(`${pathname}${search}`);
  window.location.replace(`${CANDIDATE_LOGIN_PATH}?expired=1&next=${next}`);
}

candidateApi.interceptors.response.use(
  res => res,
  err => {
    if (err.response?.status === 401 && !err.config?._skipAuthRedirect) {
      redirectToCandidateLogin();
    }
    return Promise.reject(err);
  }
);

/** Candidate sign-in. Uses publicApi so no admin bearer or refresh cookie is sent. */
export async function candidateLogin(email, password) {
  const { data } = await publicApi.post('/auth/candidate-login', { email, password });
  if (data?.accessToken) setCandidateToken(data.accessToken);
  return data;
}

/**
 * Candidate sign-up. No longer returns a token — the backend emails a
 * verification link and the account is activated by candidateVerifySignup().
 */
export async function candidateSignup(payload) {
  const { data } = await publicApi.post('/auth/candidate-signup', payload);
  return data;
}

/** Completes sign-up from the emailed verification link. */
export async function candidateVerifySignup({ token, password }) {
  const { data } = await publicApi.post('/auth/candidate-verify-signup', { token, password });
  if (data?.accessToken) setCandidateToken(data.accessToken);
  return data;
}

/** Revokes the candidate session server-side, then clears local state. */
export async function candidateLogout() {
  try {
    if (candidateToken) {
      await candidateApi.post('/auth/candidate-logout', {}, { _skipAuthRedirect: true });
    }
  } catch {
    // best effort — the token is dropped locally either way
  }
  clearCandidateToken();
}

export default candidateApi;
